import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {Subscription} from 'rxjs';
import {CountryService} from '../../../../../data-repositories/country-service';
import {Country} from '../../../../../models/country';
import {ValidationInfoService} from '../../../../../repositories/validation-info.service';
import {BrowserStorageService} from '../../../../../services/browser-storage.service';
import {ISubtituteInfo} from '../../../../../models/subtitute-info';
import {ICitizenshipInfo} from '../../../../../models/citizenship-info';
import {IAddressInfo} from '../../../../../models/address-info';
import {IPersonalInfo} from '../../../../../models/personal-info';
import {StagesNavigationService} from '../../../stages-navigation/stages-navigation.service';
import {formLog} from '../../../../../services/debug-logger';

@Component({
  standalone: false,
  selector: 'app-substitute',
  templateUrl: './substitute.component.html',
  styleUrls: ['./substitute.component.scss']
})
export class SubstituteComponent implements OnInit, OnDestroy {
  public countryList: Array<Country>;
  public substituteForm: FormGroup;
  public submitted: boolean;
  @Input() citizenshipInfo: ICitizenshipInfo;
  @Input() addressInfo: IAddressInfo;
  @Output() userModelChange = new EventEmitter<ISubtituteInfo>();
  year: string = 'yyyy';
  month: string = 'mm';
  day: string = 'dd';
  private subscription: Subscription;
  private formSub = new Subscription();

  constructor(
    countryService: CountryService,
    private validationInfo: ValidationInfoService,
    private formBuilder: FormBuilder,
    private storageService: BrowserStorageService,
    public navigationService: StagesNavigationService,
  ) {

    this.substituteForm = this.formBuilder.group({
      signatureAccept: new FormControl(this.getIsSignatureAccepted(), Validators.requiredTrue),
      signatureName: new FormControl({value: '', disabled: true})
    });

    this.patchFormFromModel();

    this.formSub.add(countryService.getCountryList().subscribe(cl => {
      this.countryList = cl;
    }));
  }

  private _signatureModel: ISubtituteInfo;

  get signatureModel(): ISubtituteInfo {
    return this._signatureModel;
  }

  @Input()
  set signatureModel(value: ISubtituteInfo) {
    this._signatureModel = value;
    this.patchFormFromModel();
    this.initialize();
  }

  private _personalInfo: IPersonalInfo;

  get personalInfo(): IPersonalInfo {
    return this._personalInfo;
  }

  @Input()
  set personalInfo(value: IPersonalInfo) {
    this._personalInfo = value;
    this.parseDateOfBirth();
    this.initialize();
  }

  getSignatureDate(): string {
    const date = new Date();
    const lang = this.storageService.getLocal('lang');
    const formatter = new Intl.DateTimeFormat(lang, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
      timeZoneName: 'short'
    });

    const formattedDate = formatter.formatToParts(date);
    const year = formattedDate.find(x => x.type === 'year').value;
    const month = formattedDate.find(x => x.type === 'month').value;
    const day = formattedDate.find(x => x.type === 'day').value;

    return `${day}/${month}/${year}`;
  }

  ngOnInit() {
    this.submitted = false;

    this.patchFormFromModel();
    this.formSub.add(this.substituteForm.valueChanges.subscribe(() => this.syncModelFromForm()));

    this.subscription = this.validationInfo.onUserUpdate.subscribe(() => {
      this.submitted = true;
      this.validationInfo.markAllFieldsAsTouched(this.substituteForm);

      if (!(this.substituteForm.valid && this.getIsSignatureAccepted())) {
        document.body.scrollTop = document.documentElement.scrollTop = 0;
      }

      this.validationInfo.validate(this.substituteForm.valid &&
        this.getIsSignatureAccepted(), 'SubstituteComponent');
    });
  }

  getIsSignatureAccepted() {
    return this.signatureModel && this.signatureModel.signatureAccept;
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.formSub.unsubscribe();
  }

  public updateUser(): void {
    this.userModelChange.emit(this.signatureModel);
  }

  private patchFormFromModel(): void {
    if (!this._signatureModel || !this.substituteForm) {
      return;
    }
    this.substituteForm.patchValue({
      signatureAccept: this._signatureModel.signatureAccept ?? ''
    }, {emitEvent: false});
    formLog.state('Substitute', this._signatureModel, this.substituteForm);
  }

  private syncModelFromForm(): void {
    if (!this._signatureModel) {
      return;
    }
    const value = this.substituteForm.getRawValue();
    this._signatureModel.signatureAccept = value.signatureAccept;
    formLog.state('Substitute', this._signatureModel, this.substituteForm);
  }

  private initialize() {
    if (!this.signatureModel || !this.personalInfo) {
      return;
    }

    if (!this.getIsSignatureAccepted()) {
      this.signatureModel.signatureDate = this.getSignatureDate();
      this.signatureModel.signatureAccept = true;
    }

    this.signatureModel.signatureName = this.personalInfo.firstName + ' ' + this.personalInfo.lastName;

    // initialize() mutates signatureAccept on the model; mirror it into the
    // reactive control (no ngModel to propagate it anymore).
    this.patchFormFromModel();
  }

  private parseDateOfBirth() {

    if (this.personalInfo.dateOfBirth) {
      const splitedDate = this.personalInfo.dateOfBirth.split('/');
      const day = Number(splitedDate[0]);
      const month = Number(splitedDate[1]);
      const year = Number(splitedDate[2]);

      if (day > 0) {

        const date = new Date(year, month - 1, day);
        const lang = this.storageService.getLocal('lang');
        const formatter = new Intl.DateTimeFormat(lang, {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: 'numeric',
          minute: 'numeric',
          hour12: true,
          timeZoneName: 'short'
        });

        const formattedDate = formatter.formatToParts(date);
        this.year = formattedDate.find(x => x.type === 'year').value;
        this.month = formattedDate.find(x => x.type === 'month').value;
        this.day = formattedDate.find(x => x.type === 'day').value;

      }
    }
  }
}
